import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import type { LedBarStepData } from '@/(routers)/boards/editor/block/definitions/fields/fieldLedBar';
import {
    registerHelperFunction,
    registerSetupCode,
    resolveBoard,
    stmt,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';

const LED_BAR_COUNT = 10;
const DEFAULT_DURATION_MS = 300;

const _ledBarGlobalCodes = new Map<string, string>();

export function ledBarGlobalCodes(): Map<string, string> {
    return _ledBarGlobalCodes;
}

export function resetLedBar(): void {
    _ledBarGlobalCodes.clear();
}

function resolveFirstPin(block: Blockly.Block): string {
    const board = resolveBoard(block) as keyof typeof BOARD_CONFIG;
    const position = block.getFieldValue('POSITION') as keyof (typeof BOARD_CONFIG)[typeof board]['pins'];
    return String(BOARD_CONFIG[board].pins[position].first);
}

function resolveSecondPin(block: Blockly.Block): string {
    const board = resolveBoard(block) as keyof typeof BOARD_CONFIG;
    const position = block.getFieldValue('POSITION') as keyof (typeof BOARD_CONFIG)[typeof board]['pins'];
    return String(BOARD_CONFIG[board].pins[position].second);
}

export function register(gen: any): void {
    gen.forBlock['led_bar'] = function (block: Blockly.Block): string {
        const firstPin = resolveFirstPin(block); // clkPin
        const secondPin = resolveSecondPin(block); // dioPin

        const barName = `bar_${firstPin}`;
        const helperName = `_sbeSetLedBarStep_${firstPin}`;

        _ledBarGlobalCodes.set(`${barName}_global`, `Grove_LED_Bar ${barName}(${firstPin}, ${secondPin}, 0);\n`);

        registerSetupCode(`${barName}_begin`, `  ${barName}.begin();\n`);

        registerHelperFunction(
            `${helperName}_def`,
            [
                `void ${helperName}(bool leds[${LED_BAR_COUNT}]) {`,
                `  for (int i = 0; i < ${LED_BAR_COUNT}; i++) {`,
                `    ${barName}.setLed(i + 1, leds[i] ? 1 : 0);`,
                `  }`,
                `}`,
            ].join('\n'),
        );

        const stepsJson = block.getFieldValue('STEPS') ?? '[]';
        let steps: LedBarStepData[] = [];
        try {
            steps = JSON.parse(stepsJson) as LedBarStepData[];
        } catch {
            steps = [];
        }

        if (steps.length === 0) return '';

        let code = '';
        for (const step of steps) {
            const normalizedLeds = Array.from({ length: LED_BAR_COUNT }, (_, idx) => Boolean(step.leds[idx]));
            const rawDuration = Number(step.duration);
            const safeDuration = Number.isFinite(rawDuration)
                ? Math.max(0, Math.trunc(rawDuration))
                : DEFAULT_DURATION_MS;
            const ledLiterals = normalizedLeds.map((v) => (v ? 'true' : 'false')).join(', ');

            code += `{\n`;
            code += `  bool _leds[${LED_BAR_COUNT}] = {${ledLiterals}};\n`;
            code += stmt(`  ${helperName}(_leds)`);
            code += stmt(`  delay(${safeDuration})`);
            code += `}\n`;
        }

        return code;
    };
}
